// Condition contract for the skip-logic condition editor.
//
// A rule's "if" side is a group ({all|any} of conditions, one nested level).
// Each leaf condition is one of the server's rules-JSON shapes:
//   { answer: { item, op, values|number|min/max } }  - an earlier question
//   { participant: { op, values } }                   - staff / pupil / parent
//   { attribute: { key, op, values } }                - see ATTRIBUTE_KEYS
// The server's parser is strict, so factories here emit exactly those keys and
// nothing else; UI-only state is kept outside the rules object.
//
// The operator-per-type table mirrors the server's ConditionSupport, so the
// editor only ever offers an operator the engine will evaluate.

import {QUESTION_TYPE} from '../constants'

// Operator -> {label, kind}. kind drives the value input:
//   'one'    a single option value (values: [v])
//   'many'   one or more option values (values: [...])
//   'number' a single number
//   'range'  a min and a max
//   'none'   no value (was not answered)
export const OPERATORS = {
  is: {label: 'is', kind: 'one'},
  isNot: {label: 'is not', kind: 'one'},
  isAnyOf: {label: 'is any of', kind: 'many'},
  isNoneOf: {label: 'is none of', kind: 'many'},
  includesAnyOf: {label: 'includes any of', kind: 'many'},
  includesAllOf: {label: 'includes all of', kind: 'many'},
  includesNoneOf: {label: 'includes none of', kind: 'many'},
  includesExactly: {label: 'includes exactly', kind: 'many'},
  eq: {label: 'equals', kind: 'number'},
  ne: {label: 'does not equal', kind: 'number'},
  gt: {label: 'is more than', kind: 'number'},
  gte: {label: 'is at least', kind: 'number'},
  lt: {label: 'is less than', kind: 'number'},
  lte: {label: 'is at most', kind: 'number'},
  between: {label: 'is between', kind: 'range'},
  notAnswered: {label: 'was not answered', kind: 'none'},
}

const CHOICE_OPS = ['is', 'isNot', 'isAnyOf', 'isNoneOf']
const MULTI_OPS = ['includesAnyOf', 'includesAllOf', 'includesNoneOf', 'includesExactly']
const NUMERIC_OPS = ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between']

const CHOICE_TYPES = [
  QUESTION_TYPE.LIKERT,
  QUESTION_TYPE.YES_NO,
  QUESTION_TYPE.YES_NO_MAYBE,
  QUESTION_TYPE.MULTIPLE_CHOICE,
]
const NUMERIC_TYPES = [
  QUESTION_TYPE.NUMBER_SLIDER,
  QUESTION_TYPE.PERCENTAGE,
  QUESTION_TYPE.LIKERT,
]
// Ranking and free-text comments cannot be tested by any condition.
const REFERENCEABLE_TYPES = [
  ...CHOICE_TYPES,
  QUESTION_TYPE.NUMBER_SLIDER,
  QUESTION_TYPE.PERCENTAGE,
  QUESTION_TYPE.MULTIPLE_SELECT,
]

export const isReferenceableType = type => REFERENCEABLE_TYPES.includes(type)

// The operators a question type accepts, in menu order.
export const operatorsForQuestionType = type => {
  const ops = []
  if (CHOICE_TYPES.includes(type)) ops.push(...CHOICE_OPS)
  if (type === QUESTION_TYPE.MULTIPLE_SELECT) ops.push(...MULTI_OPS)
  if (NUMERIC_TYPES.includes(type)) ops.push(...NUMERIC_OPS)
  if (REFERENCEABLE_TYPES.includes(type)) ops.push('notAnswered')
  return ops
}

// A choice/multi/Likert question's options as {value, label} for the value
// picker. Labels are audience-specific, falling back across audiences.
export const optionsForItem = (item, audience = 1) =>
  (item?.multiOptionResponseAttributes || [])
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(o => ({
      value: o.value,
      label:
        (audience === 2
          ? o.parentText
          : audience === 4
            ? o.pupilText
            : o.staffText) ||
        o.staffText ||
        o.parentText ||
        o.pupilText ||
        `Option ${o.value}`,
    }))

// Participant type and attribute catalogs, as the server's ParticipantAttributes
// defines them. `values` are the values a rule names (the server compares them
// without regard to case), each with the label the author sees; `free` attributes
// take whatever the school records, typed by the author. `for` is who the
// attribute is known for - for anyone else it never holds.
export const PARTICIPANT_TYPES = [
  {value: 'staff', label: 'Staff'},
  {value: 'pupil', label: 'Pupils'},
  {value: 'parent', label: 'Parents'},
]
export const JOB_ROLES = [
  {value: 'Teaching', label: 'Teaching staff'},
  {value: 'Support', label: 'Support staff'},
  {value: 'TeacherWorkingInside', label: 'Teaching staff (classroom)'},
  {value: 'TeacherWorkingOutside', label: 'Teaching staff (non-classroom)'},
  {value: 'SupportStaffWorkingInside', label: 'Support staff (classroom)'},
  {value: 'SupportStaffWorkingOutside', label: 'Support staff (non-classroom)'},
  {value: 'MiddleLeader', label: 'Middle leader'},
  {value: 'SeniorLeader', label: 'Senior leader'},
]
const YES_NO = [
  {value: 'yes', label: 'Yes'},
  {value: 'no', label: 'No'},
]
export const SEN_STATUSES = [
  {value: 'none', label: 'No SEN'},
  {value: 'senSupport', label: 'SEN support'},
  {value: 'ehcp', label: 'EHCP'},
  {value: 'statemented', label: 'Statemented'},
  {value: 'monitoring', label: 'Monitoring'},
]
export const ATTRIBUTE_KEYS = [
  {key: 'yearGroup', label: 'Year group', free: true, for: ['pupil', 'parent']},
  {key: 'jobRole', label: 'Job role', values: JOB_ROLES, for: ['staff']},
  {key: 'department', label: 'Department', free: true, for: ['staff']},
  {key: 'fsm', label: 'Free school meals', values: YES_NO, for: ['pupil']},
  {key: 'pupilPremium', label: 'Pupil premium', values: YES_NO, for: ['pupil']},
  {key: 'eal', label: 'English as an additional language', values: YES_NO, for: ['pupil']},
  {key: 'senStatus', label: 'SEN status', values: SEN_STATUSES, for: ['pupil']},
]

// The label an author sees for one of an attribute's values; a free value, or one
// this catalog does not list, is shown as it is.
export const attributeValueLabel = (key, value) =>
  ATTRIBUTE_KEYS.find(k => k.key === key)
    ?.values?.find(v => String(v.value).toLowerCase() === String(value).toLowerCase())
    ?.label ?? String(value)
export const SET_OPERATORS = [
  {name: 'isAnyOf', label: 'is any of'},
  {name: 'isNoneOf', label: 'is none of'},
]

// Classify a condition for the editor.
export const conditionKind = c => {
  if (c?.all || c?.any) return 'group'
  if (c?.answer) return 'answer'
  if (c?.participant) return 'participant'
  if (c?.attribute) return 'attribute'
  return 'unknown'
}

// Factories - v1 shapes only.
export const emptyAnswerCondition = (item, op) => {
  const kind = OPERATORS[op]?.kind
  if (kind === 'number') return {answer: {item, op, number: 0}}
  if (kind === 'range') return {answer: {item, op, min: 0, max: 0}}
  if (kind === 'none') return {answer: {item, op}}
  return {answer: {item, op, values: []}}
}
export const emptyParticipantCondition = () => ({
  participant: {op: 'isAnyOf', values: []},
})
export const emptyAttributeCondition = (key = 'yearGroup') => ({
  attribute: {key, op: 'isAnyOf', values: []},
})
export const emptyGroup = (mode = 'all') => ({[mode]: []})

// Switch an answer condition's operator, reshaping its operand to the new
// operator's kind so no stale value is left behind.
export const withAnswerOperator = (answer, op) =>
  emptyAnswerCondition(answer?.item, op)

// Whether a condition is complete enough to save (the server would reject an
// incomplete one). Groups are checked recursively.
export const isConditionComplete = c => {
  const kind = conditionKind(c)
  if (kind === 'group') {
    const conds = c.all || c.any
    return (
      Array.isArray(conds) && conds.length > 0 && conds.every(isConditionComplete)
    )
  }
  if (kind === 'answer') {
    const a = c.answer
    if (a.item == null || !a.op) return false
    const opKind = OPERATORS[a.op]?.kind
    if (opKind === 'none') return true
    if (opKind === 'number') return a.number != null
    if (opKind === 'range') return a.min != null && a.max != null
    return Array.isArray(a.values) && a.values.length > 0
  }
  if (kind === 'participant') return (c.participant.values || []).length > 0
  if (kind === 'attribute') {
    return !!c.attribute.key && (c.attribute.values || []).length > 0
  }
  return false
}

// Problems that make a condition reference something that no longer exists - the
// question it tests was deleted, or a chosen option value is gone (a renamed
// option keeps its value, so it is not flagged). Returns human-readable
// messages; groups are checked recursively.
export const conditionIssues = (condition, subjects = []) => {
  const kind = conditionKind(condition)
  if (kind === 'group') {
    const conds = condition.all || condition.any || []
    return conds.flatMap(c => conditionIssues(c, subjects))
  }
  if (kind === 'answer') {
    const a = condition.answer
    const subject = subjects.find(s => s.itemId === a.item)
    if (!subject) {
      return ['The question this condition tests is no longer available.']
    }
    const opKind = OPERATORS[a.op]?.kind
    if ((opKind === 'one' || opKind === 'many') && Array.isArray(a.values)) {
      const known = new Set((subject.options || []).map(o => o.value))
      if (a.values.some(v => !known.has(v))) {
        return ['An option this condition uses no longer exists - choose again.']
      }
    }
  }
  return []
}
